import { Input } from '@nextui-org/react'
import { FiChevronDown } from '@react-icons/all-files/fi/FiChevronDown'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  isBranchNotRequired,
  isEitherRepoBranchNotRequired,
  isNoTryExampleCommand,
  isRepoNotRequired,
  UsedCommandType
} from '~/constants/commands'
import { Connections } from '~/constants/connections'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { useIndexes } from '~/hooks'
import { useRepositoryBranches } from '~/hooks/useCodeContext'
import { RepositoryBranch } from '~/types/Conversations'
import { extractFileDisplayName } from '~/utils/repositoryUtils'
import { Button } from '../common/Button'
import { CurieSelect } from '../common/CurieSelect'
import { RepoSelectModal } from '../repo-selection/LegacyRepoSelectModal'

// Separate SearchInput component. The value is controlled by the parent's
// filter keyword so it survives the listbox remount NextUI performs when the
// filtered items change — focus restoration is handled by the parent.
const SearchInput = ({
  value,
  onChange,
  placeholder
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [localValue, setLocalValue] = useState(value)

  // Sync with parent value (also restores the text after a remount)
  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setLocalValue(newValue)
    onChange(newValue)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Only prevent blur if focus is actually moving to a dropdown item
    // and the user was actively typing (not just scrolling)
    const relatedTarget = e.relatedTarget as HTMLElement
    if (
      relatedTarget &&
      (relatedTarget.closest('[role="listbox"]') ||
        relatedTarget.closest('[data-slot="listbox"]') ||
        relatedTarget.closest('.nextui-listbox') ||
        relatedTarget.tagName === 'LI')
    ) {
      // Always maintain focus when interacting with dropdown items
      e.preventDefault()
      // Use requestAnimationFrame to avoid scroll jumping
      requestAnimationFrame(() => {
        if (inputRef.current && document.activeElement !== inputRef.current) {
          inputRef.current.focus({ preventScroll: true })
        }
      })
    }
  }

  return (
    <div className='sticky top-1 bg-white z-10'>
      <Input
        ref={inputRef}
        value={localValue}
        placeholder={placeholder}
        className='w-full px-2'
        // Show the focus ring whenever the field is focused (e.g. the autofocus
        // on open), not only on :focus-visible — otherwise the ring pops in only
        // on the first keystroke and looks inconsistent.
        classNames={{
          inputWrapper:
            'group-data-[focus=true]:z-10 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-focus group-data-[focus=true]:ring-offset-2 group-data-[focus=true]:ring-offset-background'
        }}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    </div>
  )
}

interface CodeContextSelectorProps {
  command: UsedCommandType
  setTaskRuntimeSettings: React.Dispatch<React.SetStateAction<Record<string, string | undefined>>>
  repositories: RepositoryBranch[]
  setRepositories: (repositoryBranches: RepositoryBranch[]) => void
  disabled?: boolean
}

export function CodeContextSelector({
  command,
  setTaskRuntimeSettings,
  repositories = [],
  setRepositories,
  disabled
}: CodeContextSelectorProps) {
  const { repos, getBranches } = useRepositoryBranches(command)
  const { currentWorkspace } = useWorkspaceContext()
  const primary = repositories[0]
  const repository = primary?.repository || ''
  const conn_type = primary?.conn_type || ''
  const branch = primary?.branch || ''

  const effectiveBranches = useMemo(() => {
    if (isRepoNotRequired(command)) {
      return getBranches()
    }
    if (repository && conn_type) {
      return getBranches(conn_type, repository)
    }
    return []
  }, [command, repository, conn_type, getBranches])

  // Memoize selectable repos to avoid unnecessary recalculations
  const selectableRepos = useMemo(() => repos, [repos])

  // Currently-selected repositories (multi-repo flow)
  const selectedRepoEntries = useMemo(() => repositories.filter(r => r.repository), [repositories])

  // Repo list in the shape the RepoSelectModal expects
  const selectableReposForModal = useMemo(
    () =>
      selectableRepos.map(repo => ({
        conn_type: repo.conn_type,
        index_name: repo.index_name,
        label: extractFileDisplayName(`${repo.conn_type}:${repo.index_name}`)
      })),
    [selectableRepos]
  )

  const [repoModalOpen, setRepoModalOpen] = useState(false)

  // selection state is reflected via repositories; no external selection object
  const indexes = useIndexes({ commandType: command })

  const renderLabel = useCallback(() => {
    if (isRepoNotRequired(command)) {
      return (
        <>
          Repositories and Branch:<span className='text-tokens-status-error-text'> *</span>
        </>
      )
    }
    if (isBranchNotRequired(command)) {
      return (
        <>
          Repository:<span className='text-tokens-status-error-text'> *</span>
        </>
      )
    }
    return (
      <>
        Repository and Branch:<span className='text-tokens-status-error-text'> *</span>
      </>
    )
  }, [command])

  const isIndexing = indexes.length ? 'Indexing repositories...' : null
  const fallbackRepositoryPlaceholder = isIndexing || 'No repository available'
  const fallbackBranchPlaceholder = isIndexing || 'No branch available'

  const updateTaskRuntimeSettings = useCallback(
    (repo_name: string, branch_name: string) => {
      const orgRuntimeSetting = currentWorkspace?.org_runtime_setting

      for (const index of indexes) {
        if (index.index_name === repo_name && index.branch_name === branch_name) {
          const indexRuntimeSettings = index.indexer_metadata?.runtime_setting
          // Priority: 1. Index-level XML, 2. Org-level XML (fallback)
          const taskRuntimeSettings: Record<string, string | undefined> = {
            javaVersion: indexRuntimeSettings?.java_version || '',
            mavenVersion: indexRuntimeSettings?.maven_version || '',
            muleRuntimeVersion: indexRuntimeSettings?.mule_runtime_version || '',
            settingsXmlRawTxt:
              indexRuntimeSettings?.settings_xml_raw_txt ||
              orgRuntimeSetting?.settings_xml_raw_txt ||
              '',
            settingsSecurityXmlRawTxt:
              indexRuntimeSettings?.settings_security_xml_raw_txt ||
              orgRuntimeSetting?.settings_security_xml_raw_txt ||
              '',
            vm_args: indexRuntimeSettings?.vm_args || ''
          }
          setTaskRuntimeSettings(taskRuntimeSettings)
          return
        }
      }
      // If no matching index found, use org-level XML if available
      setTaskRuntimeSettings({
        javaVersion: '',
        mavenVersion: '',
        muleRuntimeVersion: '',
        settingsXmlRawTxt: orgRuntimeSetting?.settings_xml_raw_txt || '',
        settingsSecurityXmlRawTxt: orgRuntimeSetting?.settings_security_xml_raw_txt || '',
        vm_args: ''
      })
    },
    [indexes, currentWorkspace, setTaskRuntimeSettings]
  )

  // No edit-mode initialization; parent provides repositories from context

  // Apply the modal's repo+branch selection (each repo keeps its own branch)
  const handleRepoModalConfirm = useCallback(
    (data: { repositoryBranches: RepositoryBranch[] }) => {
      setRepositories(
        data.repositoryBranches.map(r => ({
          conn_type: r.conn_type,
          repository: r.repository,
          branch: r.branch
        }))
      )
      const first = data.repositoryBranches[0]
      if (first?.repository && first.branch) {
        updateTaskRuntimeSettings(first.repository, first.branch)
      }
      setRepoModalOpen(false)
    },
    [setRepositories, updateTaskRuntimeSettings]
  )

  const [filterKeyword, setFilterKeyword] = useState('')

  // Memoize the filter function to prevent unnecessary recalculations
  const filterReposWithSearchKeyword = useCallback(
    (repos: { index_name: string; conn_type: string }[], keyword: string) => {
      if (!keyword.trim()) return repos
      return repos.filter(repo =>
        extractFileDisplayName(repo.index_name).toLowerCase().includes(keyword.toLowerCase())
      )
    },
    []
  )

  const repositorySelectItems = useMemo(() => {
    const filteredRepos = filterReposWithSearchKeyword(selectableRepos, filterKeyword)
    if (!repository || !conn_type) return filteredRepos
    const selectedRepo = selectableRepos.find(
      repo => repo.index_name === repository && repo.conn_type === conn_type
    )
    if (
      !selectedRepo ||
      filteredRepos.some(repo => repo.index_name === repository && repo.conn_type === conn_type)
    ) {
      return filteredRepos
    }
    return [selectedRepo, ...filteredRepos]
  }, [filterReposWithSearchKeyword, selectableRepos, filterKeyword, repository, conn_type])

  const searchInputComponent = useMemo(
    () => (
      <SearchInput
        value={filterKeyword}
        onChange={setFilterKeyword}
        placeholder='Search repositories'
      />
    ),
    [filterKeyword]
  )

  // When the filtered list changes, NextUI remounts the listbox in the browser
  // and the embedded search input loses focus mid-type. Re-focus it across a few
  // frames (to outlast NextUI's async rebuild) whenever the keyword changes,
  // only while the dropdown is open and focus has actually left the input — so
  // we never steal focus or jump the cursor when focus is already correct.
  useEffect(() => {
    let attempts = 0
    let raf = 0
    const tick = () => {
      const el = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement | null
      const dropdownOpen = !!document.querySelector('[role="listbox"]')
      if (el && dropdownOpen && document.activeElement !== el) {
        const end = el.value.length
        el.focus({ preventScroll: true })
        el.setSelectionRange?.(end, end)
      }
      attempts += 1
      const stillNeedsFocus =
        dropdownOpen &&
        el !== null &&
        document.activeElement !== el &&
        !!document.querySelector('[role="listbox"]')
      if (attempts < 10 && stillNeedsFocus) {
        raf = requestAnimationFrame(tick)
      }
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [filterKeyword])

  return (
    <>
      <div className='tool-tip-target repository-selector w-[1px] h-[1px] relative top-[50px] left-[500px]'></div>
      {isEitherRepoBranchNotRequired(command as UsedCommandType) ? null : (
        <div className='text-sm font-semibold text-default-700 mb-2 select-none'>
          {renderLabel()}
        </div>
      )}

      {/* Multi-repo case: a modal that pairs each repo with its own branch */}
      {isRepoNotRequired(command) ? (
        <>
          <Button
            variant='outlined'
            onClick={() => setRepoModalOpen(true)}
            isDisabled={disabled || !selectableRepos.length}
            className='w-full justify-between font-normal'
            data-testid='select-repositories-button'
          >
            <span className='truncate'>
              {selectedRepoEntries.length
                ? `${selectedRepoEntries.length} ${
                    selectedRepoEntries.length === 1 ? 'repository' : 'repositories'
                  } selected`
                : selectableRepos.length
                  ? 'Select repositories'
                  : fallbackRepositoryPlaceholder}
            </span>
            <FiChevronDown className='ml-2 shrink-0 text-tokens-neutral-icon-solid' />
          </Button>

          {selectedRepoEntries.length > 0 && (
            <div className='mt-2 rounded-2xl bg-tokens-neutral-bg-grey-1 px-4 py-3 flex flex-col gap-1'>
              {selectedRepoEntries.map((repo, index) => (
                <div
                  key={`${repo.repository}-${index}`}
                  className='flex items-center justify-between text-sm text-tokens-neutral-text-primary'
                  data-testid={`selected-repo-${index}`}
                >
                  <span className='truncate'>{extractFileDisplayName(repo.repository)}</span>
                  <span className='ml-2 shrink-0 text-tokens-neutral-text-tertiary'>
                    {repo.branch}
                  </span>
                </div>
              ))}
            </div>
          )}

          <RepoSelectModal
            isOpen={repoModalOpen}
            onClose={() => setRepoModalOpen(false)}
            onConfirm={handleRepoModalConfirm}
            repositories={selectableReposForModal}
            getBranches={getBranches}
            title='Select Repositories'
            initialSelectedRepos={repositories}
            workspaceUuid={currentWorkspace?.uuid}
          />
        </>
      ) : (
        <>
          {/* For single-repo select case (use CurieSelect for backward compatibility) */}
          <CurieSelect
            data-testid='repository-select'
            items={repositorySelectItems.map(repo => ({
              key: JSON.stringify({
                index_name: repo.index_name,
                conn_type: repo.conn_type
              }),
              label: extractFileDisplayName(repo.index_name)
            }))}
            startContents={repositorySelectItems.map(
              repo =>
                Object.values(Connections).find(conn => conn.id.toLowerCase() == repo.conn_type)
                  ?.icon || null
            )}
            placeholder={
              selectableRepos.length ? 'Select a repository' : fallbackRepositoryPlaceholder
            }
            selectedKeys={
              repository && conn_type ? [JSON.stringify({ index_name: repository, conn_type })] : []
            }
            onSelectionChange={selectedKeys => {
              if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
                return
              }
              const { index_name, conn_type: selectedConnType } = JSON.parse(
                selectedKeys.values().next().value as string
              )
              const branches = getBranches(selectedConnType, index_name)
              const selectedBranch =
                branch && branches.includes(branch) ? branch : branches[0] || ''

              setRepositories([
                { conn_type: selectedConnType, repository: index_name, branch: selectedBranch }
              ])

              updateTaskRuntimeSettings(index_name, selectedBranch)
            }}
            listProps={{
              topContent: searchInputComponent
            }}
            disabled={disabled}
          />

          <CurieSelect
            data-testid='branch-select'
            items={effectiveBranches.map(branch => ({ key: branch, label: branch }))}
            placeholder={selectableRepos.length ? 'Select a branch' : fallbackBranchPlaceholder}
            selectedKeys={[branch].filter(Boolean) as string[]}
            onSelectionChange={selectedKeys => {
              if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
                return
              }
              const selectedBranch = selectedKeys.values().next().value as string

              if (repositories[0]) {
                setRepositories([{ ...repositories[0], branch: selectedBranch }])
              }

              if (isNoTryExampleCommand(command)) {
                const githubRepo = selectableRepos.find(item => item.conn_type === 'github')
                if (githubRepo) {
                  updateTaskRuntimeSettings(githubRepo.index_name, selectedBranch)
                }
              } else if (repository) {
                updateTaskRuntimeSettings(repository, selectedBranch)
              }
              // selection sync happens via repositories state
            }}
            disabled={disabled}
          />
        </>
      )}
    </>
  )
}
